import { cva, type VariantProps } from 'class-variance-authority';
import { warnOnce } from '@/hen-ui/utils';

/**
 * The maximum column count the CVA `columns` variant supports (1–6).
 * Grid.tsx uses this to decide when to switch to the explicit-placement path
 * (inline `grid-template-columns`) rather than a CVA class. Co-locating it
 * here with `gridVariants` prevents the threshold from drifting if the variant
 * map changes.
 */
export const GRID_MAX_CVA_COLUMNS = 6;

/** A px or rem length, such as `300px`. Anything else is ignored. */
const GRID_MIN_COLUMN_WIDTH = /^\d+(?:\.\d+)?(?:px|rem)$/;

/** A length, or `1fr` so leftover space is split equally. */
const GRID_MAX_COLUMN_WIDTH = /^(?:\d+(?:\.\d+)?(?:px|rem)|1fr)$/;

/**
 * Accepts a CSS length such as `300px`. Anything else is ignored so a
 * document cannot inject a declaration through this prop.
 */
export function parseGridMinColumnWidth(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const width = value.trim();
  return GRID_MIN_COLUMN_WIDTH.test(width) ? width : undefined;
}

/**
 * Accepts a CSS length or `1fr`. Anything else is ignored so a document
 * cannot inject a declaration through this prop.
 */
export function parseGridMaxColumnWidth(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const width = value.trim();
  return GRID_MAX_COLUMN_WIDTH.test(width) ? width : undefined;
}

/**
 * One horizontal row at every width. The minimum track size is
 * `--grid-min-column`, set from `minColumnWidth`. `--grid-max-column` caps it
 * when `maxColumnWidth` is set; otherwise the track stops at the row width
 * minus 3rem so the next item peeks. Keep this string in sync with the admin
 * canvas transcription (`data-repeater-layout-classes.ts`).
 */
export const GRID_MIN_COLUMN_WIDTH_CLASS = [
  'min-w-0',
  'max-w-full',
  'overflow-x-auto',
  '[grid-auto-flow:column]',
  '[grid-auto-columns:minmax(var(--grid-min-column),var(--grid-max-column,calc(100%_-_3rem)))]',
  '[scroll-snap-type:x_mandatory]',
  '[&>*]:[scroll-snap-align:start]',
].join(' ');

/**
 * Gap token → utility class map, shared by the CVA `gap` variant and the
 * explicit-placement render path so both stay token-driven (no hardcoded
 * spacing) and always agree on the same set of gaps.
 */
export const GRID_GAP_CLASSES = {
  xxs: 'gap-xxs',
  xs: 'gap-xs',
  sm: 'gap-sm',
  md: 'gap-md',
  lg: 'gap-lg',
  xl: 'gap-xl',
  '2xl': 'gap-2xl',
  '3xl': 'gap-3xl',
  max: 'gap-max',
} as const;

export type GridGap = keyof typeof GRID_GAP_CLASSES;

/**
 * Resolve a gap token to its utility class, defaulting to `lg` (matching the
 * CVA `defaultVariants`). Used by the Grid's explicit-placement path, which
 * sets `grid-template-columns` inline and therefore can't lean on the column
 * CVA variant, but still wants the same token-driven gap.
 *
 * Hardens against invalid runtime values (JS consumers, `any`, deserialized
 * data) by falling back to `lg` when the key is absent, and emitting a dev
 * warning once so misconfigured gap tokens surface during development.
 */
export function gridGapClass(gap?: GridGap | null): string {
  // null / undefined = "not provided" → silent default.
  if (gap == null) return GRID_GAP_CLASSES.lg;
  const resolved = GRID_GAP_CLASSES[gap];
  if (resolved === undefined) {
    warnOnce(
      `Grid/gap-invalid:${String(gap)}`,
      `Grid: unknown \`gap\` token "${String(gap)}"; falling back to "lg".`,
    );
    return GRID_GAP_CLASSES.lg;
  }
  return resolved;
}

/**
 * Grid variant styles using Class Variance Authority (CVA)
 *
 * Defines responsive column and gap options for the Grid component.
 * All styles use design tokens from the HEN Design System.
 *
 * ## Columns (base, responsive at md, lg, and xl breakpoints)
 * - 1 through 6 columns
 *
 * ## Gap Options
 * - xxs through 3xl and max, matching design-system spacing tokens (`gap-*`)
 */
export const gridVariants = cva('grid', {
  variants: {
    columns: {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-4',
      5: 'grid-cols-5',
      6: 'grid-cols-6',
    },
    'columns-md': {
      1: 'md:grid-cols-1',
      2: 'md:grid-cols-2',
      3: 'md:grid-cols-3',
      4: 'md:grid-cols-4',
      5: 'md:grid-cols-5',
      6: 'md:grid-cols-6',
    },
    'columns-lg': {
      1: 'lg:grid-cols-1',
      2: 'lg:grid-cols-2',
      3: 'lg:grid-cols-3',
      4: 'lg:grid-cols-4',
      5: 'lg:grid-cols-5',
      6: 'lg:grid-cols-6',
    },
    'columns-xl': {
      1: 'xl:grid-cols-1',
      2: 'xl:grid-cols-2',
      3: 'xl:grid-cols-3',
      4: 'xl:grid-cols-4',
      5: 'xl:grid-cols-5',
      6: 'xl:grid-cols-6',
    },
    layout: {
      feature: 'w-full md:grid-cols-2 lg:grid-cols-feature',
    },
    gap: GRID_GAP_CLASSES,
  },
  defaultVariants: {
    columns: 1,
    gap: 'lg',
  },
});

export type GridVariantProps = VariantProps<typeof gridVariants>;
